"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Zap, Maximize2, Cog, ShieldCheck, ArrowRight, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface ChecklistItem {
  id: string;
  stepNumber: string;
  title: string;
  specLabel: string;
  specValue: string;
  primaryRequirement: string;
  detail: string;
  bulletOne: string;
  bulletTwo: string;
  iconName: "zap" | "maximize" | "cog" | "shield";
}
const checklistData: ChecklistItem[] = [];
export default function Inspection() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="what-to-check-before-buying" data-nav="light" className="border-t border-border bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between border-b border-border pb-8 mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-mono text-sm uppercase tracking-wider text-muted-foreground font-medium">
                Buyer Preflight Checklist
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-tight">
              What to Check Before Buying a Mechanical Bull
            </h2>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg leading-relaxed">
              The easiest way to make a poor mechanical bull purchase is to compare machines only by price.
            </p>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg leading-relaxed">
              Before you buy a mechanical bull, check the information that affects setup, operation and ownership.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  1. Drive and motion system
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Understand what system the machine uses and what movements it is designed to provide. Do not assume two bulls operate the same way because they look similar.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  2. Controller
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Check the controller type and available operating modes. For used equipment, ask whether the exact controller shown comes with the machine.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  3. Emergency stop and safety functions
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Identify the emergency-stop system and any other documented safety features for that particular model.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  4. Electrical requirements
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Do not assume every mechanical bull uses the same voltage or electrical setup. Verify the exact machine and blower requirements before planning installation.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  5. Complete operating footprint
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  The mechanical base is only part of the space you need. Consider the inflated safety area plus appropriate surrounding clearance.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  6. Rider limits
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Use the manufacturer&apos;s specification for the exact model rather than applying a generic rider limit across every machine.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  7. What&apos;s included
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Confirm whether the advertised package includes:
                </p>
                <ul className="mt-1.5 list-disc pl-5 space-y-0.5 marker:text-primary text-muted-foreground text-base sm:text-lg leading-relaxed">
                  <li>mechanical bull</li>
                  <li>motion base</li>
                  <li>controller</li>
                  <li>inflatable safety area</li>
                  <li>blower</li>
                  <li>cables</li>
                  <li>accessories</li>
                  <li>documentation</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  8. Parts availability
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  A machine is easier to own when you know how to source the controller, switches, inflatable components and other parts it may eventually need.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  9. Service and post-sale support
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  Review the documented service and support terms for the specific new or used product instead of assuming all machines receive identical coverage.
                </p>
              </div>

              <div>
                <p className="font-semibold text-foreground text-base sm:text-lg">
                  10. Shipping dimensions and weight
                </p>
                <p className="mt-0.5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                  This becomes especially important if you plan to transport the machine regularly.
                </p>
              </div>
            </div>
          </div>

          <div className="shrink-0 flex items-center">
            <Button asChild variant="outline" className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif text-base uppercase tracking-wider font-semibold h-11 px-5">
              <Link to="/MechanicalBullBuyingGuidePage">
                <span>Complete buying guide</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 4-Column Technical Sequence */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {checklistData.map((item, index) => <motion.article key={item.id} data-index={index} variants={itemVariants} className="group border-r border-b border-border bg-card p-6 sm:p-7 flex flex-col justify-between transition-[background-color,border-color] duration-150 hover:bg-muted/30">
              <div>
                {/* Step & Spec Tag Line */}
                <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                  <span className="font-mono text-2xl font-semibold text-primary">
                    {item.stepNumber}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground border border-border px-2 py-1 bg-background">
                    {item.specLabel}
                  </span>
                </div>

                {/* Icon & Title */}
                <div className="mb-4">
                  <div className="size-10 border border-border bg-background flex items-center justify-center mb-4 text-foreground group-hover:border-foreground transition-colors duration-150">
                    {item.iconName === "zap" && <Zap className="size-5 text-primary" />}
                    {item.iconName === "maximize" && <Maximize2 className="size-5 text-primary" />}
                    {item.iconName === "cog" && <Cog className="size-5 text-primary" />}
                    {item.iconName === "shield" && <ShieldCheck className="size-5 text-primary" />}
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground leading-snug">
                    {item.title}
                  </h3>
                </div>

                {/* Machine Spec Plate */}
                <div className="border border-border bg-muted/50 p-3 mb-5">
                  <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-wider">
                    Baseline Metric
                  </div>
                  <div className="text-sm font-mono font-bold text-foreground mt-0.5 tracking-tight">
                    {item.specValue}
                  </div>
                </div>

                {/* Summary Detail */}
                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                  {item.detail}
                </p>

                {/* Spec Checklist Points */}
                <ul className="space-y-3 pt-4 border-t border-border/80">
                  <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                    <span className="leading-tight">{item.bulletOne}</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                    <span className="leading-tight">{item.bulletTwo}</span>
                  </li>
                </ul>
              </div>

              {/* Card Footer Metric Marker */}
              <div className="mt-8 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono uppercase">
                <span>Phase {item.stepNumber}</span>
                <span className="text-foreground font-medium">{item.primaryRequirement}</span>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Industrial Blueprint Callout Box */}
        
      </div>
    </section>;
}